import { useCallback, useMemo, useRef, useState } from 'react'
import { CalendarStep } from './types/dimmingProfile.types'
import { EditorGeometry } from './types/presenter.types'

// Fixed plot geometry (the SVG scales via viewBox in the view). Defined locally
// so the calendar editor does not depend on the profile editor's GEOMETRY.
const GEOMETRY: EditorGeometry = {
  width: 720,
  height: 260,
  marginLeft: 44,
  marginRight: 16,
  marginTop: 16,
  marginBottom: 36,
  plotWidth: 720 - 44 - 16,
  plotHeight: 260 - 16 - 36,
}

// Zenith axis domain (degrees) and level domain (%). Mirrors the profile
// editor's MAX_TIME_MINUTES / MAX_LEVEL but on a zenith axis.
export const ZEN_MIN = 80
export const ZEN_MAX = 120
const LEVEL_MIN = 0
const LEVEL_MAX = 100

// Vertical inset of the LEVEL scale inside the plot: 0% and 100% map a few px
// AWAY from the plot border so handles/plateaus at the extremes stay visible —
// a 0% step used to sit exactly ON the bottom border, indistinguishable from
// the frame. The 0/100 gridlines move with the scale (they use levelToY too).
export const LEVEL_INSET_TOP = 8
export const LEVEL_INSET_BOTTOM = 12
const LEVEL_SPAN = GEOMETRY.plotHeight - LEVEL_INSET_TOP - LEVEL_INSET_BOTTOM

// Snap a zenith value to integer degrees and clamp to [ZEN_MIN, ZEN_MAX].
const snapZenith = (zenith: number): number => {
  const snapped = Math.round(zenith)
  return Math.min(ZEN_MAX, Math.max(ZEN_MIN, snapped))
}

// Clamp a dimming level to 0..100 (integer).
const clampLevel = (level: number): number =>
  Math.min(LEVEL_MAX, Math.max(LEVEL_MIN, Math.round(level)))

// Sort calendar steps ascending by zenith (immutably).
const sortSteps = (steps: CalendarStep[]): CalendarStep[] =>
  [...steps].sort((a, b) => a.zenith_angle_deg - b.zenith_angle_deg)

// Whether a candidate zenith would collide with an existing step (same snapped
// degree), optionally ignoring the step at `exceptIndex` (the one dragged).
export const hasZenithCollision = (
  steps: CalendarStep[],
  zen: number,
  exceptIndex?: number,
): boolean =>
  steps.some((s, i) => i !== exceptIndex && s.zenith_angle_deg === zen)

// State exposed by the presenter to the view.
export interface CalendarStepsState {
  steps: CalendarStep[] // always sorted ascending by zenith
  selectedStepIndex: number | null
  draggingStepIndex: number | null
  geometry: EditorGeometry
}

// Actions the view can invoke (same names/shape as the profile actions).
export interface CalendarStepsActions {
  addStepAt: (zenith: number, level: number) => void
  removeStep: (index: number) => void
  selectStep: (index: number | null) => void
  setStepLevel: (index: number, level: number) => void
  setStepZenith: (index: number, zenith: number) => void
  beginDrag: (index: number) => void
  dragTo: (clientX: number, clientY: number) => void
  endDrag: () => void
  handlePlotClick: (clientX: number, clientY: number) => void
  zenToX: (zenith: number) => number
  levelToY: (level: number) => number
}

interface UseCalendarStepsParams {
  steps: CalendarStep[]
  onChange: (next: CalendarStep[]) => void
  svgRef: React.RefObject<SVGSVGElement>
  // Mirror the zenith axis (ZEN_MAX on the left). Used by the SUNRISE editor so
  // TIME flows left→right on both charts: at dawn the sun moves toward LOWER
  // zenith angles, which read right-to-left on the natural axis — operators
  // consistently misread that as "inverted".
  reversed?: boolean
}

/**
 * Presenter hook for the CalendarStepsCurve editor. Mirrors
 * useDimmingProfileEditorPresenter but on a ZENITH axis (degrees): the editor is
 * controlled, `steps` is the current array and `onChange` emits the next
 * (sorted) array on every edit. Zenith snaps to integer degrees and is clamped
 * to [ZEN_MIN, ZEN_MAX]; level clamps to 0..100.
 */
export const useCalendarStepsPresenter = ({
  steps: rawSteps,
  onChange,
  svgRef,
  reversed = false,
}: UseCalendarStepsParams): {
  state: CalendarStepsState
  actions: CalendarStepsActions
} => {
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null)
  const [draggingStepIndex, setDraggingStepIndex] = useState<number | null>(null)
  // Identité du step en cours de drag : son ZÉNITH (unique — deux steps ne
  // partagent jamais un degré snappé), PAS son index. `emit` re-trie à chaque
  // mouvement ; un index capturé au beginDrag devient périmé dès qu'un drag
  // rapide franchit un voisin, et le mouvement suivant éditerait le MAUVAIS step
  // (le handle « saute »). On re-résout l'index depuis ce zénith à chaque
  // dragTo. Voir revue MR !2 (#3 drag crossing).
  const draggingZenithRef = useRef<number | null>(null)

  const steps = useMemo(() => sortSteps(rawSteps), [rawSteps])

  // ── coordinate conversions ────────────────────────────────────────────────
  const zenToX = useCallback(
    (zenith: number): number => {
      const ratio = (zenith - ZEN_MIN) / (ZEN_MAX - ZEN_MIN)
      const r = reversed ? 1 - ratio : ratio
      return GEOMETRY.marginLeft + r * GEOMETRY.plotWidth
    },
    [reversed],
  )

  const levelToY = useCallback((level: number): number => {
    const ratio = level / LEVEL_MAX
    return GEOMETRY.marginTop + LEVEL_INSET_TOP + (1 - ratio) * LEVEL_SPAN
  }, [])

  // Convert a pointer position (client coords) into snapped (zenith, level).
  const clientToData = useCallback(
    (clientX: number, clientY: number): { zenith: number; level: number } => {
      const svg = svgRef.current
      if (!svg) return { zenith: ZEN_MIN, level: 0 }
      const rect = svg.getBoundingClientRect()
      // Map screen px -> viewBox units (viewBox is 0..GEOMETRY.width).
      const vbX = ((clientX - rect.left) / rect.width) * GEOMETRY.width
      const vbY = ((clientY - rect.top) / rect.height) * GEOMETRY.height
      const ratioX = (vbX - GEOMETRY.marginLeft) / GEOMETRY.plotWidth
      const rx = reversed ? 1 - ratioX : ratioX
      const ratioY = (vbY - GEOMETRY.marginTop - LEVEL_INSET_TOP) / LEVEL_SPAN
      const zenith = snapZenith(ZEN_MIN + rx * (ZEN_MAX - ZEN_MIN))
      const level = clampLevel((1 - ratioY) * LEVEL_MAX)
      return { zenith, level }
    },
    [svgRef, reversed],
  )

  // ── emit helper ───────────────────────────────────────────────────────────
  const emit = useCallback(
    (nextSteps: CalendarStep[]) => {
      onChange(sortSteps(nextSteps))
    },
    [onChange],
  )

  // ── step actions ──────────────────────────────────────────────────────────
  const addStepAt = useCallback(
    (zenith: number, level: number) => {
      const z = snapZenith(zenith)
      const lvl = clampLevel(level)
      const existing = steps.findIndex((s) => s.zenith_angle_deg === z)
      if (existing >= 0) {
        // UPSERT: clicking an occupied zenith degree REPLACES that step's
        // level at the clicked height (mirrors CalendarCurve's background
        // click) — it used to silently ignore the click, which read as "the
        // chart doesn't work". The step keeps its zenith, so indices/sort
        // are unchanged and the step stays selected for fine-tuning.
        emit(
          steps.map((s, i) =>
            i === existing ? { ...s, dimming_level_percent: lvl } : s,
          ),
        )
        setSelectedStepIndex(existing)
        return
      }
      emit([...steps, { zenith_angle_deg: z, dimming_level_percent: lvl }])
    },
    [steps, emit],
  )

  const removeStep = useCallback(
    (index: number) => {
      const next = steps.filter((_, i) => i !== index)
      emit(next)
      setSelectedStepIndex(null)
    },
    [steps, emit],
  )

  const setStepLevel = useCallback(
    (index: number, level: number) => {
      const clamped = clampLevel(level)
      const next = steps.map((s, i) =>
        i === index ? { ...s, dimming_level_percent: clamped } : s,
      )
      emit(next)
    },
    [steps, emit],
  )

  const setStepZenith = useCallback(
    (index: number, zenith: number) => {
      const z = snapZenith(zenith)
      if (hasZenithCollision(steps, z, index)) return
      const next = steps.map((s, i) =>
        i === index ? { ...s, zenith_angle_deg: z } : s,
      )
      emit(next)
    },
    [steps, emit],
  )

  // ── drag lifecycle ────────────────────────────────────────────────────────
  const beginDrag = useCallback(
    (index: number) => {
      draggingZenithRef.current = steps[index]?.zenith_angle_deg ?? null
      setDraggingStepIndex(index)
      setSelectedStepIndex(index)
    },
    [steps],
  )

  const dragTo = useCallback(
    (clientX: number, clientY: number) => {
      const trackedZen = draggingZenithRef.current
      if (trackedZen === null) return
      // Re-résout l'index depuis l'identité zénith (l'array a pu être re-trié
      // par un mouvement précédent) — évite d'éditer le mauvais step.
      const index = steps.findIndex((s) => s.zenith_angle_deg === trackedZen)
      if (index < 0) return
      const { zenith, level } = clientToData(clientX, clientY)
      // Reject horizontal move that collides with another step's slot.
      const z = hasZenithCollision(steps, zenith, index)
        ? steps[index].zenith_angle_deg
        : zenith
      const next = steps.map((s, i) =>
        i === index
          ? { ...s, zenith_angle_deg: z, dimming_level_percent: level }
          : s,
      )
      const sorted = sortSteps(next)
      // Suit le step dragué par son nouveau zénith à travers le re-tri.
      draggingZenithRef.current = z
      setDraggingStepIndex(sorted.findIndex((s) => s.zenith_angle_deg === z))
      onChange(sorted)
    },
    [steps, clientToData, onChange],
  )

  const endDrag = useCallback(() => {
    draggingZenithRef.current = null
    setDraggingStepIndex(null)
  }, [])

  const handlePlotClick = useCallback(
    (clientX: number, clientY: number) => {
      const { zenith, level } = clientToData(clientX, clientY)
      addStepAt(zenith, level)
    },
    [clientToData, addStepAt],
  )

  const selectStep = useCallback((index: number | null) => {
    setSelectedStepIndex(index)
  }, [])

  const state: CalendarStepsState = {
    steps,
    selectedStepIndex,
    draggingStepIndex,
    geometry: GEOMETRY,
  }

  const actions: CalendarStepsActions = {
    addStepAt,
    removeStep,
    selectStep,
    setStepLevel,
    setStepZenith,
    beginDrag,
    dragTo,
    endDrag,
    handlePlotClick,
    zenToX,
    levelToY,
  }

  return { state, actions }
}
